import { useFavoriteTab } from '../../hooks/useFavoriteTab';
/**
 * APPOINTMENT SNAPSHOTS — the day's scheduled board at 8 AM, 12 PM and 4 PM, what moved
 * since 8 AM and who moved it, and each order's service failure for the day.
 *
 * The Scheduler / LC grid computes live, so "what was on the board at 8am" is gone by
 * 8:01. A Sync job freezes the board three times a day (same-day appointments only, GWI
 * dispatchers only — the grid's own population) and diffs each later capture against the
 * day's FIRST one, attributing each added / removed appointment to the cms.history row
 * that made the change. This page only reads what was frozen; nothing here recomputes.
 *
 * THE QUESTION IT ANSWERS is "we had X appointments at 8 AM, why is it Y at noon / 4 PM?",
 * and the layout follows that (Lisa, 2026-10-05):
 *
 *  1. ONE DAY AT A TIME. The date is the filter bar's first control; the cards are that
 *     day's slots plus, for today, "Current" (the live board, re-pulled every five
 *     minutes) — the time big, the capture clock small, "563 Deliveries / 65 Prepulls",
 *     and on the later slots the added / removed since 8 AM. A slot whose hour has not
 *     come yet is not drawn; "Not captured" means the job slept through that hour, and
 *     the slot is never back-filled, because a 1pm capture labelled "12 PM" would be a lie.
 *
 *  2. ONE TABLE PER SLOT, tabbed Deliveries / Prepulls. On 12 PM, 4 PM and Current the
 *     table is that board PLUS the orders that were on the baseline board and are gone:
 *     BLUE rows were added since the baseline, RED rows were removed, and the changed
 *     rows lead the table. The baseline is 8 AM (the day's first capture) unless the
 *     "Changes since" selector beside the tabs picks another capture — "since 12 PM" on
 *     the 4 PM card shows only the afternoon's moves. At 4 PM the job also freezes
 *     TOMORROW's board; on the next day that is the FIRST card, "Prev 4 PM", the
 *     8 AM card diffs against it so the after-hours moves show, and the selector offers
 *     it on every card. A future day has that card (once last night's freeze exists) and
 *     its Current board diffed against it, no 8 / 12 / 4 cards yet, and nothing on it can
 *     be "past appt window" yet. "Change" names
 *     the kind of change (moved to another day, appt cleared, order closed, appt set, …)
 *     "Changed By" / "Changed At" the person and time from cms.history, "Changed Via"
 *     where it was done (Scheduler, Manual (360), Manual (V1), Change All, …); when nothing
 *     logged the change (EDI import, leg renumbered, order closed) "Changed By" shows
 *     what the live order said instead and the other two are blank. The chips beside the
 *     tabs — added, removed, past appt window — filter the table to those rows, and every
 *     local filter (chips, column filters, search) resets when the day changes. Beside
 *     them, "Hide unchanged" drops everything that was on the baseline board and is
 *     still here, which is added AND removed at once — no chip does that.
 *
 *  3. SERVICE FAILURES RIDE ON THE ROW — the order's controllable ("C:") reschedule
 *     reason and type for the day, no separate card; a removal that looks like a
 *     reschedule with nothing coded reads "No SF coded". A row whose appointment window
 *     closed 30+ minutes before the capture without arriving carries a "past window" badge.
 *
 *  3b. A DELIVERY TURNED AWAY IS STILL A DELIVERY (Lisa, 2026-10-12). When a consignee
 *     refuses the load the order is re-legged — a stopoff parks the container at a yard
 *     and the delivery moves to a later date — which used to take it off that day's
 *     board and re-list it the same day as a PREPULL, because a drop before the delivery
 *     leg is exactly what the grid calls a prepull. It now stays a DELIVERY on the day it
 *     was refused, badged "Rejected" in the Status column, and shows again on its new
 *     date badged "Re-delivery". Because it never leaves the first day's board it is not
 *     counted as removed there; the add on the new date is left alone, since by then
 *     the reschedule is usually already in that day's baseline capture.
 *
 *  4. THE FILTER BAR IS THE SCHEDULER'S. Region (the order dispatcher's region), LC
 *     (order LC or warehouse LC, groups expand), Dispatcher (the order's dispatcher),
 *     Customer and Move Type change which rows the server returns; every other column is
 *     on the row, and the grid filters it locally. The dropdowns are FACETED: each one
 *     offers only the values the captured boards actually hold under the OTHER
 *     dropdowns' picks, so Houston lists Houston's dispatchers and nobody else's. Each
 *     has a "Select All" beside its "All …" row — tick everything, then untick the one
 *     yard or dispatcher you don't want.
 *
 *  5. ADDED COLUMNS ARE LIVE. Extra order fields picked in the column editor come from the
 *     order as it is now, beside values frozen at capture time.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import FilterBar from '../../components/FilterBar';
import DateTimePicker from '../../components/DateTimePicker';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import {
  useAdditionalFieldColumns,
  useAdditionalFieldSelection,
} from '../../hooks/useAdditionalFields';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import lcApptSnapshotsService, {
  AppointmentSnapshotFilters,
  AppointmentSnapshotsResponse,
  SnapshotKind,
  SnapshotLegStatus,
  SnapshotRow,
  SnapshotSlot,
} from '../../services/lcApptSnapshots.service';

/** One key for the grid's settings AND the saved extra-field selection — the hook
 *  namespaces the latter as `additional_fields_<key>`. */
const SETTINGS_KEY = 'appointment-snapshots';

/** How often the Current card re-pulls the live board while the tab is visible. The
 *  refresh is quiet (no spinner) and flagged `background` so it files no report-run row. */
const LIVE_REFRESH_MS = 5 * 60 * 1000;

/** What the chips beside the tabs narrow the table to. Service failures have no chip:
 *  the Service Failure column shows the reason or "No SF coded", and its own column
 *  filter already answers both questions. */
type RowFilter = 'ADDED' | 'REMOVED' | 'LATE';

/** `hideUnchanged` is the "Hide unchanged" switch beside the chips: drop every order
 *  that was on the baseline board and is still on this one, leaving added and removed
 *  together — the chips only ever show one of the two (Lisa, 2026-10-12). */
const rowPasses = (r: SnapshotRow, f: RowFilter | null, hideUnchanged: boolean): boolean => {
  if (hideUnchanged && !r.change_type) return false;
  switch (f) {
    case 'ADDED': return r.change_type === 'ADDED';
    case 'REMOVED': return r.change_type === 'REMOVED';
    case 'LATE': return r.late_risk;
    default: return true;
  }
};

/** "2 moved to another day · 1 appt cleared" — the reason buckets behind a count. */
export const reasonBreakdown = (rows: SnapshotRow[]): string => {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const key = (r.change_reason || 'unexplained').toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([reason, n]) => `${n.toLocaleString()} ${reason}`)
    .join(' · ');
};

/** Local calendar date as YYYY-MM-DD. NOT toISOString(): that converts to UTC first, so
 *  a date picked west of UTC comes out as the previous day. */
const ymd = (d: Date | null): string | undefined => {
  if (!d) return undefined;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** "8 AM" / "12 PM" / "4 PM" from a Central hour; a negative code is the night-before
 *  capture the job took at the previous day's last slot: -8 → "Prev 4 PM". Short enough
 *  to sit on one line in the card title beside its stamp, which carries the date
 *  ("captured 10/06 4:00 PM"); the card's hover spells out "the day before". */
export const slotLabel = (hour: number): string => {
  if (hour < 0) return `Prev ${slotLabel(hour + 24)}`;
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
};

/** The key `rows` is indexed by, and the card's name. The live card is "Current" on
 *  today and on a future day, "Latest" on a past day — not "Final", because an
 *  appointment can still be moved off a past date, but not "Current" either. */
const slotKey = (s: SnapshotSlot): string => (s.live ? 'live' : String(s.slot_hour));
export const slotName = (s: SnapshotSlot, isCurrent: boolean): string =>
  (s.live ? (isCurrent ? 'Current' : 'Latest') : slotLabel(s.slot_hour ?? 0));

/** YYYY-MM-DD → a local-midnight Date for the picker (no UTC shift). */
const fromYmd = (s: string): Date => new Date(`${s}T00:00:00`);

/** YYYY-MM-DD (or a datetime string) as MM/DD/YYYY. Slicing beats parsing, which would
 *  re-introduce the timezone shift `ymd` exists to avoid. */
/** MM/DD from a date or datetime string, same slicing. */
const md = (v: string | null | undefined): string => (v ? `${String(v).slice(5, 7)}/${String(v).slice(8, 10)}` : '');

const mdy = (v: string | null | undefined): string => {
  if (!v) return '';
  const s = String(v);
  return `${s.slice(5, 7)}/${s.slice(8, 10)}/${s.slice(0, 4)}`;
};

/** A stored datetime as "MM/DD HH:MM" — the day is already on the page, so the year
 *  and the seconds would only widen the column. */
const shortDateTime = (v: string | null | undefined): string => {
  if (!v) return '';
  const s = String(v);
  return `${s.slice(5, 7)}/${s.slice(8, 10)} ${s.slice(11, 16)}`.trim();
};

/** Just the clock part of a stored datetime, as "8:04 AM". */
const clock = (v: string | null | undefined): string => {
  if (!v) return '';
  const hh = Number(String(v).slice(11, 13));
  const mm = String(v).slice(14, 16);
  if (Number.isNaN(hh)) return '';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${mm} ${hh < 12 ? 'AM' : 'PM'}`;
};

const apptWindow = (start: string | null, end: string | null): string => {
  if (start && end) return `${start} – ${end}`;
  return start || end || '';
};

/** Status chips, colour-coded so the leg's progress reads at a glance (Lisa, 2026-10-05):
 *  green = arrived, blue = under way, amber = driver assigned but nothing started,
 *  red = no driver yet. DISPATCHED / DEPARTED are the pre-2026-10-05 capture values. */
export const STATUS_META: Record<SnapshotLegStatus, { label: string; cls: string }> = {
  ARRIVED: { label: 'Arrived', cls: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200' },
  STARTED: { label: 'Started', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200' },
  NOT_STARTED: { label: 'Not Started', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200' },
  NOT_DISPATCHED: { label: 'Not Dispatched', cls: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200' },
  DEPARTED: { label: 'Arrived', cls: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200' },
  DISPATCHED: { label: 'Dispatched', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  '': { label: '', cls: '' },
};

/** The "turned away at the consignee" badge, or null when nothing was refused.
 *
 *  Same order, two days, two readings: on the day of the refusal the row IS the attempt
 *  ("Rejected"); on the delivery that was rescheduled out of it, the badge says the
 *  attempt is behind it ("Re-delivery") and the hover gives the date. Exported so the
 *  test can pin both without rendering the grid. */
export const rejectedBadge = (r: SnapshotRow): { label: string; cls: string; title: string } | null => {
  if (!r.rejected_date) return null;
  const sameDay = r.rejected_date === r.appt_date;
  return sameDay
    ? {
      label: 'Rejected',
      cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200',
      title: 'The delivery was attempted and turned away at the consignee this day. It stays on '
        + 'this board as a delivery; the container went to a yard and the delivery was rescheduled.',
    }
    : {
      label: 'Re-delivery',
      cls: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200',
      title: `Rescheduled after the delivery was turned away at the consignee on ${mdy(r.rejected_date)}.`,
    };
};

const num = (v: number | null | undefined) => Number(v ?? 0).toLocaleString();

/** Row tint: blue = added since the baseline, red = on the baseline board but gone.
 *  Exported so the test can pin the two colours to the two change types. */
export const rowTint = (row: SnapshotRow): string => {
  if (row.change_type === 'REMOVED') return 'bg-red-50 dark:bg-red-950/40';
  if (row.change_type === 'ADDED') return 'bg-blue-50 dark:bg-blue-950/40';
  return '';
};

/** The Change cell's text. A reschedule shows the date it went to ("Resch 10/07") rather
 *  than the bucket name — the date is what the reader wants next. The date comes from the
 *  history row; when nothing logged the change it is read out of the live-state note. */
export const changeLabel = (r: SnapshotRow): string => {
  if (!r.change_type) return '';
  const fromNote = (prefix: string) => (r.note || '').match(new RegExp(`^${prefix} (\\d{2}/\\d{2})`))?.[1] ?? '';
  switch (r.change_reason) {
    case 'Moved to another day': return `Resch ${md(r.new_appt_date) || fromNote('Moved to')}`.trim();
    case 'Moved from another day': return `Resch from ${md(r.old_appt_date) || fromNote('Moved from')}`.trim();
    default: return r.change_reason || (r.change_type === 'ADDED' ? 'Added' : 'Removed');
  }
};

/** Who changed it. Falls back to the raw userid when AD has no name, and to the note
 *  when nothing logged the change at all ("Order closed …", "Appt cleared (no history
 *  found)") so the cell still says what the live order showed. */
export const changedByName = (row: SnapshotRow): string => {
  if (!row.change_type) return '';
  return row.changed_by_name || row.changed_by || row.note || '';
};

/** When it changed, blank when nothing logged the change. Its own column — Lisa,
 *  2026-10-06 — so the time sorts and filters apart from the person. */
export const changedAtText = (row: SnapshotRow): string =>
  row.change_type ? shortDateTime(row.changed_at) : '';

const plural = (n: number, one: string, many: string) => `${num(n)} ${n === 1 ? one : many}`;

function SlotCard({ slot, active, isCurrent, onClick }: { slot: SnapshotSlot; active: boolean; isCurrent: boolean; onClick: () => void }) {
  const cls = [
    'w-64 rounded-lg border px-3 py-2 text-left transition',
    active
      ? 'bg-blue-600 border-blue-600 text-white shadow-md'
      : slot.captured
        ? 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:shadow-sm'
        : 'bg-gray-50 dark:bg-gray-900/60 border-dashed border-gray-300 dark:border-gray-700 text-gray-500 hover:border-gray-400',
  ].join(' ');
  const muted = active ? 'text-white/80' : 'text-gray-500 dark:text-gray-400';
  const hasBaseline = slot.captured && slot.baseline_slot_hour !== null;
  const name = slotName(slot, isCurrent);
  // The night-before capture (negative code) was taken on the PREVIOUS calendar day, so
  // its stamp carries the date; the day's own slots need only the time.
  const dayBefore = slot.slot_hour !== null && slot.slot_hour < 0;
  const title = slot.live
    ? (isCurrent
      ? 'The board as the live tables have it right now, computed when the page loaded; refreshes every five minutes.'
      : 'This day\'s board as the live tables have it now. Not final — an appointment can still be moved off a past date — but the latest word.')
    : dayBefore
      ? `This day's deliveries and prepulls as they stood at ${slotLabel((slot.slot_hour as number) + 24)} Central the day before — what the 8 AM card is compared with, so the after-hours moves show.`
      : `Deliveries and prepulls scheduled for the day under GWI dispatchers (the Scheduler grid's population), as they stood at ${name} Central.`;
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className={cls}
      title={title + (hasBaseline ? ` Added / removed are against the ${slotLabel(slot.baseline_slot_hour as number)} capture.` : '')}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xl font-bold leading-none">{name}</span>
        <span className={`text-[11px] whitespace-nowrap ${muted}`}>
          {!slot.captured ? 'Not captured'
            : slot.live ? `as of ${clock(slot.captured_at)}`
              : dayBefore ? `captured ${md(slot.captured_at)} ${clock(slot.captured_at)}`
                : `captured ${clock(slot.captured_at)}`}
        </span>
      </div>
      <div className={`mt-1.5 text-sm leading-none ${slot.captured ? '' : muted}`}>
        {slot.captured ? (
          <>
            <span className="font-mono text-lg font-semibold">{num(slot.filtered_delivery_count)}</span>
            {' '}{slot.filtered_delivery_count === 1 ? 'Delivery' : 'Deliveries'}
            {' '}<span className={`mx-1 ${muted}`}>/</span>{' '}
            <span className="font-mono text-lg font-semibold">{num(slot.filtered_prepull_count)}</span>
            {' '}{slot.filtered_prepull_count === 1 ? 'Prepull' : 'Prepulls'}
          </>
        ) : '— Deliveries / — Prepulls'}
      </div>
      {/* The answer to "why is it different from the baseline", in the row colours the table uses. */}
      {hasBaseline && (
        // Deliveries and prepulls together; the chips beside the tabs carry the selected
        // kind's share (a per-kind split here was tried and dropped — Lisa, 2026-10-06).
        <div className={`mt-1.5 flex items-center gap-3 text-[11px] ${active ? 'text-white/90' : 'text-gray-600 dark:text-gray-300'}`}
          title="Deliveries and prepulls together, since the baseline capture; the chips beside the tabs show the selected kind">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-blue-200 dark:bg-blue-700" aria-hidden="true" />
            {plural(slot.filtered_added, 'added', 'added')}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-200 dark:bg-red-700" aria-hidden="true" />
            {plural(slot.filtered_removed, 'removed', 'removed')}
          </span>
        </div>
      )}
    </button>
  );
}

function AppointmentSnapshotsContent() {
  // null = "today", decided by the SERVER on every fetch (the DB's Central date), so a
  // fresh load always lands on the current day and a tab left open across midnight rolls
  // over on its next refresh. Set only when the user picks a date; clearing goes back.
  const [date, setDate] = useState<Date | null>(null);
  const [regions, setRegions] = useState<string[]>([]);
  const [lcs, setLcs] = useState<string[]>([]);
  const [dispatchers, setDispatchers] = useState<string[]>([]);
  const [customers, setCustomers] = useState<string[]>([]);
  const [movetypes, setMovetypes] = useState<string[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [kind, setKind] = useState<SnapshotKind>('DELIVERY');
  useFavoriteTab(kind, setKind, ['DELIVERY', 'PREPULL'], true, kind === 'DELIVERY' ? 'Deliveries' : 'Prepulls');
  // "Changes since": the capture the added / removed are measured against. null = the
  // server's default, the day's first capture. Kept across cards and days like the
  // selected card is; the server falls back to the first capture where the choice does
  // not apply, and each card reports the baseline it actually used.
  const [baselineHour, setBaselineHour] = useState<number | null>(null);
  // Clicking a chip beside the tabs ("24 added", "3 removed", "5 past appt window")
  // narrows the table to those rows; clicking again clears it. Reset whenever the card
  // or the day changes.
  const [rowFilter, setRowFilter] = useState<RowFilter | null>(null);
  // "Hide unchanged": drop the orders that were on the baseline board and are still
  // here, leaving only what moved. Rendered (and applied) only on a card that HAS a
  // baseline — on the 8 AM card there is nothing to be unchanged against.
  const [hideUnchanged, setHideUnchanged] = useState(false);

  // The column editor's extra order fields. The SELECTION feeds the fetch below; the
  // generated COLUMNS need the column defs built further down — hence the two halves.
  const {
    additionalFields,
    onAdditionalFieldsChange,
    ready: fieldsReady,
  } = useAdditionalFieldSelection(SETTINGS_KEY);

  const [data, setData] = useState<AppointmentSnapshotsResponse | null>(null);
  const [filters, setFilters] = useState<AppointmentSnapshotFilters | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  // The server-side filters, as the API wants them. One object so the rows fetch and the
  // faceted options fetch cannot drift apart.
  const serverFilters = useMemo(() => ({
    terminal: regions.length ? regions.join(',') : undefined,
    lc: lcs.length ? lcs.join(',') : undefined,
    dispatcher: dispatchers.length ? dispatchers.join(',') : undefined,
    customer: customers.length ? customers.join(',') : undefined,
    movetype: movetypes.length ? movetypes.join(',') : undefined,
  }), [regions, lcs, dispatchers, customers, movetypes]);

  // The dropdowns are FACETED: every selection re-asks the server which options still
  // have data under the others ("Houston" leaves Houston's dispatchers — Lisa,
  // 2026-10-12). Each list keeps its own picks, so this never takes a choice away.
  useEffect(() => {
    lcApptSnapshotsService.filters(serverFilters)
      .then(setFilters)
      .catch(() => setFilters(null));
  }, [serverFilters]);

  // `quiet` is the Current card's timer: no spinner over a table the reader is looking at,
  // and `background` so the server does not file a report-run row per tick.
  const load = useCallback((opts: { quiet?: boolean } = {}) => {
    if (!opts.quiet) setLoading(true);
    setError(null);
    lcApptSnapshotsService.list({
      date: ymd(date),
      ...serverFilters,
      baseline: baselineHour ?? undefined,
      additional_fields: additionalFields.length ? additionalFields.join(',') : undefined,
      ...(opts.quiet ? { background: true } : {}),
    })
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || e?.message || 'Failed to load'))
      .finally(() => { if (!opts.quiet) setLoading(false); });
  }, [date, serverFilters, baselineHour, additionalFields]);

  // Held on `ready`: until the saved column selection is known, loading would fetch
  // once without the user's extra fields and again with them.
  useEffect(() => { if (fieldsReady) load(); }, [load, fieldsReady]);

  // A tab that was hidden across midnight: when it comes back, a "today" page (no date
  // picked) re-fetches so the server moves it to the new day.
  useEffect(() => {
    const onVisible = () => { if (!document.hidden && date === null && fieldsReady) load({ quiet: true }); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [date, fieldsReady, load]);

  const snapshots = data?.snapshots ?? [];
  // Today or a future day: the live card is "Current" and the page lands on it. A past
  // day: "Latest", and the page lands on the day's last capture.
  const isCurrent = !!data && data.date >= data.today;
  // The day the server answered for. A "removed" chip clicked on 10/06 must not still be
  // narrowing 10/05's table (a day with no baseline has no removed rows: the table went
  // blank — Lisa, 2026-10-06). The grid's own column filters and search reset the same
  // way, by keying the table on this.
  const loadedDate = data?.date ?? '';
  useEffect(() => { setRowFilter(null); }, [loadedDate]);

  // Land on "Current" for today; on a past day on the last captured slot (the day's final
  // capture is the record, "Latest" is the follow-up), falling back to whatever exists.
  // Only when no slot is chosen yet, or the chosen one does not exist for the loaded day.
  useEffect(() => {
    if (!data) return;
    if (selectedKey !== null && snapshots.some((s) => slotKey(s) === selectedKey)) return;
    const live = snapshots.find((s) => s.live);
    const lastCaptured = [...snapshots].reverse().find((s) => s.captured && !s.live);
    const pick = (isCurrent ? live : lastCaptured) ?? lastCaptured ?? live ?? snapshots[0];
    setSelectedKey(pick ? slotKey(pick) : null);
  }, [data, snapshots, selectedKey, isCurrent]);

  const activeSlot: SnapshotSlot | undefined = selectedKey !== null
    ? snapshots.find((s) => slotKey(s) === selectedKey)
    : undefined;
  // The capture this card's added / removed are against, as the server resolved it.
  const baseline = activeSlot?.baseline_slot_hour ?? null;
  const baselineLabel = slotLabel(baseline ?? 8);
  // The earlier captures this card could be measured against — the server's list, which
  // includes the night-before capture (negative code) that is never a card. The selector
  // shows whenever there is at least one, even alone, so every card with a diff reads
  // the same way (Lisa, 2026-10-06).
  const baselineOptions = activeSlot
    ? (data?.baseline_options ?? []).filter((h) => activeSlot.live || h < (activeSlot.slot_hour ?? 0))
    : [];

  const regionOptions = useMemo<MultiSelectOption[]>(
    () => (filters?.regions ?? []).map((r) => ({ value: r, label: r })),
    [filters]);
  const lcOptions = useMemo<MultiSelectOption[]>(
    () => (filters?.lcs ?? []).map((o) => ({ value: o.value, label: o.label })),
    [filters]);
  const dispatcherOptions = useMemo<MultiSelectOption[]>(
    () => (filters?.dispatchers ?? []).map((o) => ({ value: o.value, label: o.label })),
    [filters]);
  const customerOptions = useMemo<MultiSelectOption[]>(
    () => (filters?.customers ?? []).map((o) => ({ value: o.value, label: o.label })),
    [filters]);
  const movetypeOptions = useMemo<MultiSelectOption[]>(
    () => (filters?.movetypes ?? []).map((o) => ({ value: o.value, label: o.label })),
    [filters]);

  const hasFilters = !!(regions.length || lcs.length || dispatchers.length || customers.length
    || movetypes.length);
  const clearAll = () => {
    setRegions([]); setLcs([]); setDispatchers([]); setCustomers([]); setMovetypes([]);
  };

  const openPro = useCallback((pro: number) => setSelectedPro(pro), []);

  // Column order follows how the page is read: what the order is, when and where it is
  // going, how far along it is, then what changed and who changed it, then the service
  // failure, and only then the reference columns (region and dispatcher are also filters).
  // Sparse cells render empty rather than as dashes — most rows have no change and no
  // failure, and a dash in every one of them is what made the grid read as noise.
  const columns = useMemo<ColumnDef<SnapshotRow>[]>(() => [
    {
      accessorKey: 'pronumber',
      header: 'PRO',
      cell: ({ getValue }) => {
        const pro = getValue() as number;
        return (
          <button
            type="button"
            onClick={() => openPro(pro)}
            className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
          >
            {pro}
            <ArrowTopRightOnSquareIcon className="w-3 h-3" />
          </button>
        );
      },
    },
    { accessorKey: 'customer', header: 'Customer' },
    {
      accessorKey: 'lc_name',
      header: 'LC',
      cell: ({ row, getValue }) => (getValue() as string) || row.original.csr || '',
    },
    {
      id: 'appt_window',
      header: 'Appt Time',
      accessorFn: (r) => apptWindow(r.appt_time_start, r.appt_time_end),
    },
    { accessorKey: 'location', header: 'Location' },
    {
      id: 'leg_status',
      header: 'Status',
      // Wide enough for "Not Dispatched" plus a badge on one line.
      size: 290,
      // The badges are part of the accessor, not just the cell: the search box and the
      // column filter read accessor values, so "Rejected" has to be in there to be
      // findable — which is the whole point of marking it.
      accessorFn: (r) => [STATUS_META[r.leg_status ?? '']?.label ?? '',
        rejectedBadge(r)?.label ?? '', r.late_risk ? 'past window' : ''].filter(Boolean).join(' · '),
      cell: ({ row }) => {
        const meta = STATUS_META[row.original.leg_status ?? ''];
        const rejected = rejectedBadge(row.original);
        if (!meta?.label && !rejected) return '';
        return (
          <span className="inline-flex items-center gap-1.5">
            {meta?.label && (
              <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap ${meta.cls}`}>
                {meta.label}
              </span>
            )}
            {rejected && (
              <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap ${rejected.cls}`}
                title={rejected.title}>
                {rejected.label}
              </span>
            )}
            {row.original.late_risk && (
              <span className="inline-block rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200"
                title="The appointment window closed more than 30 minutes before this capture and the leg had not arrived.">
                past window
              </span>
            )}
          </span>
        );
      },
    },
    {
      // One column for what happened: the kind of change, coloured like the row. A
      // delivery moved to tomorrow, one whose appt was cleared and one whose order closed
      // are three different conversations, so the reason IS the label; the hover keeps
      // the specific note ("Moved to 10/07"). Kept as a column, not just a row colour,
      // because colour does not survive the Excel export or a column filter.
      id: 'change',
      header: 'Change',
      accessorFn: changeLabel,
      cell: ({ row, getValue }) => {
        const r = row.original;
        if (!r.change_type) return '';
        const label = getValue() as string;
        const cls = r.change_type === 'ADDED'
          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
          : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200';
        const title = [r.change_type === 'ADDED' ? `Added since the ${baselineLabel} capture` : `Removed since the ${baselineLabel} capture`,
          r.note, r.change_source ? `via ${r.change_source}` : '',
          r.old_appt_date || r.new_appt_date
            ? `${mdy(r.old_appt_date) || 'blank'} → ${mdy(r.new_appt_date) || 'blank'}` : '']
          .filter(Boolean).join(' · ');
        return (
          <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap ${cls}`} title={title}>
            {label}
          </span>
        );
      },
    },
    {
      id: 'changed_by',
      header: 'Changed By',
      accessorFn: changedByName,
      cell: ({ row, getValue }) => {
        const r = row.original;
        const text = getValue() as string;
        if (!text) return '';
        const unexplained = !(r.changed_by_name || r.changed_by);
        return (
          <span className={`block max-w-[16rem] truncate ${unexplained ? 'italic text-gray-500 dark:text-gray-400' : ''}`}
            title={unexplained ? 'Nothing in cms.history logged this change; this is what the live order showed.' : r.change_source ? `via ${r.change_source}` : undefined}>
            {text}
          </span>
        );
      },
    },
    {
      id: 'changed_at',
      header: 'Changed At',
      accessorFn: changedAtText,
      size: 130,
      cell: ({ getValue }) => <span className="whitespace-nowrap">{getValue() as string}</span>,
    },
    {
      // Scheduler or manual — Lisa, 2026-10-06. The server reads it off the history type
      // (and, for cmsapi saves, off whether the Scheduler logged the same pro at the same
      // time); the raw type stays on hover.
      accessorKey: 'change_via',
      header: 'Changed Via',
      size: 120,
      cell: ({ row, getValue }) => {
        const v = getValue() as string;
        return v ? <span className="whitespace-nowrap" title={row.original.change_source || undefined}>{v}</span> : '';
      },
    },
    {
      // The reason only ("C: DISPATCH ISSUE"), blank when none — Lisa, 2026-10-05. The
      // type, who coded it and the comments stay on hover.
      accessorKey: 'sf_reason',
      header: 'Service Failure',
      cell: ({ row, getValue }) => {
        const r = row.original;
        const v = (getValue() as string) || '';
        if (!v) return '';
        const title = [
          r.sf_type ? `Type: ${r.sf_type}` : '',
          r.sf_created_by_name || r.sf_created_by
            ? `Created by ${r.sf_created_by_name || r.sf_created_by}${r.sf_created_at ? ` ${shortDateTime(r.sf_created_at)}` : ''}` : '',
          r.sf_comments ? `Comments: ${r.sf_comments}` : '',
          r.sf_count > 1 ? `${r.sf_count} failures logged this day (latest shown)` : '',
        ].filter(Boolean).join('\n');
        return (
          <span className="block max-w-[18rem] truncate text-red-700 dark:text-red-300" title={title || undefined}>
            {v}{r.sf_count > 1 ? ` (+${r.sf_count - 1})` : ''}
          </span>
        );
      },
    },
    { accessorKey: 'region', header: 'Region' },
    { accessorKey: 'dispatcher', header: 'Dispatcher' },
    { accessorKey: 'container', header: 'Container' },
    { accessorKey: 'movetype', header: 'Move Type' },
  ], [openPro, baselineLabel]);

  const { columns: columnsWithExtras, availableFields } =
    useAdditionalFieldColumns<SnapshotRow>(
      { additionalFields, onAdditionalFieldsChange, ready: fieldsReady },
      { baseColumns: columns },
    );

  const slotRows = activeSlot ? (data?.rows[slotKey(activeSlot)] ?? []) : [];
  // What changed comes first — removed, then added — then the rest of the board by
  // appointment time. The one row that answers the question must not sit at the bottom
  // of 560 others.
  const tableRows = useMemo(() => {
    const rank = (r: SnapshotRow) => (r.change_type === 'REMOVED' ? 0 : r.change_type === 'ADDED' ? 1 : 2);
    return slotRows
      .filter((r) => r.kind === kind && rowPasses(r, rowFilter, hideUnchanged && baseline !== null))
      .sort((a, b) => rank(a) - rank(b)
        || (a.appt_time_start || '99').localeCompare(b.appt_time_start || '99')
        || a.pronumber - b.pronumber);
  }, [slotRows, kind, rowFilter, hideUnchanged, baseline]);
  const dayLabel = mdy(data?.date ?? ymd(date) ?? '');
  // What the picker shows: the user's pick, else the day the server answered for.
  const pickerDate: Date | null = date ?? (data ? fromYmd(data.date) : null);

  // The Current card keeps itself fresh while someone is looking at it.
  useVisibilityAwareInterval(() => load({ quiet: true }), activeSlot?.live ? LIVE_REFRESH_MS : null, false);

  // Per-kind counts for the tabs and the chips beside them.
  const tabFor = (k: SnapshotKind) => {
    const rows = slotRows.filter((r) => r.kind === k);
    const addedRows = rows.filter((r) => r.change_type === 'ADDED');
    const removedRows = rows.filter((r) => r.change_type === 'REMOVED');
    return {
      current: rows.length - removedRows.length,
      added: addedRows.length,
      removed: removedRows.length,
      addedWhy: reasonBreakdown(addedRows),
      removedWhy: reasonBreakdown(removedRows),
      late: rows.filter((r) => r.late_risk).length,
    };
  };
  const tabs: { key: SnapshotKind; label: string }[] = [
    { key: 'DELIVERY', label: 'Deliveries' },
    { key: 'PREPULL', label: 'Prepulls' },
  ];
  const kindPlural = kind === 'DELIVERY' ? 'Deliveries' : 'Prepulls';
  const tableTitle = activeSlot
    ? (activeSlot.live ? `${slotName(activeSlot, isCurrent)} ${kindPlural}` : `${kindPlural} at ${slotName(activeSlot, isCurrent)}`)
    : kindPlural;

  return (
    <div className="p-4 space-y-3 h-full flex flex-col">
      <div className="shrink-0">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Appointment Snapshots
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          The day&apos;s scheduled deliveries and prepulls as of 4 PM the day before, 8 AM, 12 PM, 4 PM and now.
          <span className="ml-2 text-blue-700 dark:text-blue-300">Blue</span> = added,
          <span className="ml-1 text-red-700 dark:text-red-300">red</span> = removed since the {baselineLabel} snapshot.
        </p>
      </div>

      <FilterBar wrap label="Appt Date:">
        <DateTimePicker
          mode="date"
          selectionType="single"
          value={pickerDate}
          onChange={(val) => setDate((val as Date | null) ?? null)}
          inputVariant="compact"
          inputClassName="w-32"
          labelType="none"
          placeholder="Date"
          ariaLabel="Appt Date"
          autoApply
        />
        <FilterBar.Divider />
        {/* Every dropdown offers Select All beside its "All …" (clear) row: ticking
            everything and then unticking the one you don't want — "all the Houston
            dispatchers except Barbour's Cut" — is the ask these filters get (Lisa,
            2026-10-12), and it is not expressible by picking from an empty box. */}
        <MultiSelect
          options={regionOptions}
          value={regions}
          onChange={setRegions}
          placeholder="All Regions"
          showClearOption
          clearOptionLabel="All Regions"
          showSelectAllOption
          enableSearch
          searchPlaceholder="Search regions…"
          size="sm"
        />
        <MultiSelect
          options={lcOptions}
          value={lcs}
          onChange={setLcs}
          placeholder="All LCs"
          showClearOption
          clearOptionLabel="All LCs"
          showSelectAllOption
          enableSearch
          searchPlaceholder="Search LCs…"
          preserveOrder
          size="sm"
        />
        <MultiSelect
          options={dispatcherOptions}
          value={dispatchers}
          onChange={setDispatchers}
          placeholder="All Dispatchers"
          showClearOption
          clearOptionLabel="All Dispatchers"
          showSelectAllOption
          enableSearch
          searchPlaceholder="Search dispatchers…"
          size="sm"
        />
        <MultiSelect
          options={customerOptions}
          value={customers}
          onChange={setCustomers}
          placeholder="All Customers"
          showClearOption
          clearOptionLabel="All Customers"
          showSelectAllOption
          enableSearch
          searchPlaceholder="Search customers…"
          size="sm"
        />
        <MultiSelect
          options={movetypeOptions}
          value={movetypes}
          onChange={setMovetypes}
          placeholder="All Move Types"
          showClearOption
          clearOptionLabel="All Move Types"
          showSelectAllOption
          enableSearch
          searchPlaceholder="Search move types…"
          size="sm"
        />
        {hasFilters && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Clear filters
          </button>
        )}
      </FilterBar>

      {data && (
        <div className="shrink-0 flex flex-wrap gap-2">
          {snapshots.map((s) => (
            <SlotCard
              key={slotKey(s)}
              slot={s}
              isCurrent={isCurrent}
              active={selectedKey === slotKey(s)}
              onClick={() => { setSelectedKey(slotKey(s)); setRowFilter(null); }}
            />
          ))}
        </div>
      )}

      {error && !loading && (
        <p role="alert" className="shrink-0 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {activeSlot && !activeSlot.captured ? (
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No snapshot was captured for {slotName(activeSlot, isCurrent)} on {dayLabel}.
          </p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Sheet-style tabs, as on Unbilled Charges: Deliveries / Prepulls with the count
              on the board and, once there is a baseline, the +/− since it. */}
          <div className="shrink-0 flex items-end gap-0.5 overflow-x-auto">
            {tabs.map((t) => {
              const { current } = tabFor(t.key);
              const active = kind === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setKind(t.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-t border whitespace-nowrap ${
                    active
                      ? 'bg-panel text-blue-700 dark:text-blue-300 border-gray-300 dark:border-gray-600 border-b-white dark:border-b-gray-800 -mb-px font-semibold'
                      : 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 border-transparent hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t.label} ({num(current)})
                </button>
              );
            })}
            {/* The selected tab's own counts — deliveries OR prepulls; the card above carries
                the day's total. Each chip is a toggle that narrows the table to just those
                rows. Added / removed need a baseline; "past appt window" is about the board
                itself and shows on every card. */}
            {activeSlot?.captured && (() => {
              const t = tabFor(kind);
              // The reason breakdown ("1 moved to another day · 1 order closed") lives on
              // the hover: the Change column already shows it per row, and inline it made
              // the chip a sentence.
              const chip = (type: RowFilter, count: number, label: string, swatch: string, why?: string) => {
                const on = rowFilter === type;
                const action = count === 0 ? `No ${label} rows` : on ? 'Show all rows' : `Show only the ${label} rows`;
                return (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={on}
                    disabled={count === 0}
                    onClick={() => setRowFilter(on ? null : type)}
                    title={why && count > 0 ? `${why}. ${action}.` : action}
                    className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 transition ${
                      on
                        ? 'bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900'
                        : count === 0
                          ? 'text-gray-400 dark:text-gray-500 cursor-default'
                          : 'hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block w-2.5 h-2.5 rounded-sm ${swatch}`} aria-hidden="true" />
                    <span className="font-semibold">{num(count)}</span> {label}
                  </button>
                );
              };
              return (
                <span data-testid="change-legend" className="ml-3 mb-1 inline-flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  {baseline !== null && chip('ADDED', t.added, 'added', 'bg-blue-200 dark:bg-blue-700', t.addedWhy || undefined)}
                  {baseline !== null && chip('REMOVED', t.removed, 'removed', 'bg-red-200 dark:bg-red-700', t.removedWhy || undefined)}
                  {chip('LATE', t.late, 'past appt window', 'bg-amber-300 dark:bg-amber-600')}
                  {/* Added and removed at once, which no single chip gives: drop every
                      order that sat on the baseline board and is still sitting here. */}
                  {baseline !== null && (
                    <label className="inline-flex items-center gap-1 cursor-pointer select-none"
                      title={`Hide the orders that were on the ${baselineLabel} board and are still on this one, leaving only what moved.`}>
                      <input
                        type="checkbox"
                        checked={hideUnchanged}
                        onChange={(e) => setHideUnchanged(e.target.checked)}
                        className="h-3 w-3 rounded border-gray-300 dark:border-gray-600"
                      />
                      Hide unchanged
                    </label>
                  )}
                </span>
              );
            })()}
            {/* "Changes since": rebase this card's added / removed on any earlier capture —
                "since 12 PM" on the 4 PM card is just the afternoon's moves. Shown on
                every card that has a baseline, one option or three, for consistency. */}
            {baselineOptions.length > 0 && (
              <label className="ml-auto mb-1 inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                Changes since
                <select
                  aria-label="Changes since"
                  value={baseline ?? ''}
                  onChange={(e) => setBaselineHour(Number(e.target.value))}
                  className="rounded border border-theme-border-primary bg-panel px-1.5 py-0.5 text-xs"
                >
                  {baselineOptions.map((h) => <option key={h} value={h}>{slotLabel(h)}</option>)}
                </select>
              </label>
            )}
          </div>
          <div className="flex-1 min-h-0">
            <TanTable
              // A new day starts the grid over: column filters and the search box are the
              // table's own state, and a remount is the one way to clear them. Saved
              // layout (widths, order, extra fields) rides on settingsKey and survives.
              key={loadedDate}
              data={tableRows}
              columns={columnsWithExtras}
              loading={loading}
              errorMessage={error}
              onRetry={load}
              // ONE saved layout for both tabs: Deliveries and Prepulls have the same
              // columns, and a width dragged on one must hold when you switch to the other.
              settingsKey={SETTINGS_KEY}
              cardTitle={tableTitle}
              searchPlaceholder={`Search ${kindPlural.toLowerCase()}…`}
              enableColumnFilters
              enableGrouping
              trackViewedColumnId="pronumber"
              getRowClassName={rowTint}
              availableFields={availableFields}
              additionalFields={additionalFields}
              onAdditionalFieldsChange={onAdditionalFieldsChange}
              fillContainer
            />
          </div>
        </div>
      )}

      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={selectedPro !== null}
        onClose={() => setSelectedPro(null)}
      />
    </div>
  );
}

function AppointmentSnapshots() {
  return (
    <ViewedProsProvider>
      <AppointmentSnapshotsContent />
    </ViewedProsProvider>
  );
}

export default AppointmentSnapshots;
